// ============================================================
// DockerImages.jsx
// ============================================================
// Junior-dev Notes:
// - Concept: Images are "Snapshots" made of multiple layers.
// - Key Term: "Copy-on-Write" (CoW) and Layer Caching.
// - UI: Stacked visual representation of a Dockerfile.
// - Branding: prodevconsult.com
// ============================================================

import React from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Button,
    Stack,
    styled,
    Divider,
    Paper,
    Chip,
} from "@mui/material";
import { Link } from "react-router-dom";

/* MUI Icons */
import LayersIcon from "@mui/icons-material/Layers";
import StorageIcon from "@mui/icons-material/Storage";
import SpeedIcon from "@mui/icons-material/Speed";
import HistoryIcon from "@mui/icons-material/History";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

/* ── Brand tokens ────────────────────────────────────────── */
const DOCKER_BLUE = "#0DB7ED";
const NAVY_BG = "#001E2B";
const TEXT_WHITE = "#FFFFFF";

const BRAND = {
    muted: "rgba(255,255,255,0.6)",
    surface: "rgba(255,255,255,0.03)",
    border: "rgba(13, 183, 237, 0.2)",
    domain: "prodevconsult.com"
};

/* ── Styled Components ───────────────────────────────────── */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: NAVY_BG,
    backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)`,
    backgroundSize: "40px 40px",
    color: TEXT_WHITE,
    paddingTop: theme.spacing(12),
    paddingBottom: theme.spacing(10),
}));

const LayerCard = styled(Paper)(({ theme, level }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    backgroundColor: `rgba(13, 183, 237, ${0.05 * level})`,
    border: `1px solid rgba(13, 183, 237, ${0.1 * level})`,
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    transition: "0.3s",
    "&:hover": {
        transform: "translateX(10px)",
        backgroundColor: "rgba(13, 183, 237, 0.2)",
    }
}));

const WritableLayer = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    backgroundColor: "rgba(0, 237, 100, 0.1)",
    border: "2px dashed #00ED64",
    borderRadius: "4px",
    textAlign: "center"
}));

export default function DockerImages() {
    return (
        <PageWrapper>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 8, textAlign: { xs: "center", md: "left" } }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, justifyContent: { xs: "center", md: "flex-start" } }}>
                        <Chip
                            label="Core Concepts · Module 05"
                            sx={{ bgcolor: "rgba(13, 183, 237, 0.1)", color: DOCKER_BLUE, fontWeight: 700 }}
                        />
                        <Typography variant="caption" sx={{ color: DOCKER_BLUE, fontWeight: 800, letterSpacing: 1 }}>
                            {BRAND.domain.toUpperCase()}
                        </Typography>
                    </Stack>

                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Images & <span style={{ color: DOCKER_BLUE }}>Layers</span>
                    </Typography>
                    <Typography variant="h6" sx={{ color: BRAND.muted, maxWidth: 800, lineHeight: 1.6 }}>
                        A Docker image is a read-only template. Every instruction in a Dockerfile
                        creates a new layer. Understanding this stack is the key to fast builds.
                    </Typography>
                </Box>



                {/* 2. VISUALIZING THE STACK */}
                <Grid container spacing={6} alignItems="flex-start">
                    <Grid item xs={12} md={6}>
                        <Typography variant="h5" fontWeight={800} sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
                            <LayersIcon sx={{ color: DOCKER_BLUE }} /> Anatomy of a Running Container
                        </Typography>

                        {/* Writable Layer (Container Layer) */}
                        <WritableLayer>
                            <Typography variant="subtitle2" sx={{ color: "#00ED64", fontWeight: 900 }}>
                                WRITABLE LAYER (Container)
                            </Typography>
                            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
                                Temporary storage for app data, logs, and changes. Deleted when container is removed.
                            </Typography>
                        </WritableLayer>

                        {/* Read-Only Layers (The Image) */}
                        <LayerCard level={5}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>CMD ["npm", "start"]</Typography>
                            <Chip label="Layer 4" size="small" variant="outlined" sx={{ color: DOCKER_BLUE }} />
                        </LayerCard>
                        <LayerCard level={3}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>COPY . .</Typography>
                            <Chip label="Layer 3" size="small" variant="outlined" sx={{ color: DOCKER_BLUE }} />
                        </LayerCard>
                        <LayerCard level={2}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>RUN npm install</Typography>
                            <Chip label="Layer 2" size="small" variant="outlined" sx={{ color: DOCKER_BLUE }} />
                        </LayerCard>
                        <LayerCard level={1}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>FROM node:18-alpine</Typography>
                            <Chip label="Base Layer" size="small" variant="outlined" sx={{ color: DOCKER_BLUE }} />
                        </LayerCard>

                        <Typography variant="caption" sx={{ mt: 2, display: 'block', color: BRAND.muted, textAlign: 'center' }}>
                            ↑ All Image Layers are <strong>Immutable (Read-Only)</strong> ↑
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Typography variant="h5" fontWeight={800} sx={{ mb: 4 }}>Why Layers Matter?</Typography>

                        <Stack spacing={4}>
                            <Box sx={{ display: 'flex', gap: 3 }}>
                                <SpeedIcon sx={{ color: DOCKER_BLUE, fontSize: 32 }} />
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Build Caching</Typography>
                                    <Typography variant="body2" sx={{ color: BRAND.muted }}>
                                        Docker reuses layers that haven't changed. If you only change your code,
                                        Docker skips the <code>npm install</code> layer, making builds nearly instant.
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 3 }}>
                                <StorageIcon sx={{ color: DOCKER_BLUE, fontSize: 32 }} />
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Storage Efficiency</Typography>
                                    <Typography variant="body2" sx={{ color: BRAND.muted }}>
                                        If 10 different images use <code>ubuntu:20.04</code> as a base,
                                        Docker only stores that base layer <strong>once</strong> on your hard drive.
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 3 }}>
                                <HistoryIcon sx={{ color: DOCKER_BLUE, fontSize: 32 }} />
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Intermediate Layers</Typography>
                                    <Typography variant="body2" sx={{ color: BRAND.muted }}>
                                        You can view the history of any image to see exactly how it was built
                                        and which command created which layer.
                                    </Typography>
                                </Box>
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>

                {/* 3. PRO-TIP: THE CACHE TRAP */}
                <Paper sx={{
                    mt: 10,
                    p: 4,
                    bgcolor: "rgba(255, 255, 255, 0.02)",
                    border: `1px solid ${BRAND.border}`,
                    borderRadius: 4
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, color: DOCKER_BLUE }}>
                        💡 Junior Pro-Tip: Order Matters!
                    </Typography>
                    <Typography variant="body2" sx={{ color: BRAND.muted, lineHeight: 1.7, mb: 2 }}>
                        Docker builds from top to bottom. If a layer changes, <strong>every layer below it</strong> in the Dockerfile
                        must be rebuilt.
                    </Typography>
                    <Typography variant="body2" sx={{ color: TEXT_WHITE, fontWeight: 700 }}>
                        Action: Always put frequently changing commands (like COPY code) at the BOTTOM of your Dockerfile.
                    </Typography>
                </Paper>

                <Divider sx={{ bgcolor: "rgba(255,255,255,0.05)", my: 8 }} />

                {/* 4. FOOTER */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="h5" sx={{ mb: 4, fontWeight: 700 }}>Now, let's write our first Dockerfile.</Typography>
                    <Link to="/docker-file" style={{ textDecoration: "none" }}>
                        <Button
                            variant="contained"
                            size="large"
                            endIcon={<ArrowForwardIcon />}
                            sx={{
                                bgcolor: DOCKER_BLUE,
                                color: NAVY_BG,
                                fontWeight: 800,
                                px: 8,
                                py: 2,
                                borderRadius: "4px",
                                boxShadow: `0 4px 14px rgba(13, 183, 237, 0.3)`,
                                "&:hover": { bgcolor: "#0091ca" }
                            }}
                        >
                            Next: Dockerfile Deep-Dive
                        </Button>
                    </Link>
                    <Typography variant="caption" display="block" sx={{ mt: 4, opacity: 0.3 }}>
                        Authorized Content by {BRAND.domain}
                    </Typography>
                </Box>

            </Container>
        </PageWrapper>
    );
}
